'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/providers/i18n';
import { cn } from '@/lib/utils';
import { Brush, Eraser, Trash2, Undo2, FlipHorizontal2 } from 'lucide-react';
import { getEffectiveImageConfig, getImageModelIdsByTag, IMAGE_MODEL_TAGS, imageModels } from '@flowmuse/models/image';
import { useModelLabelById } from '@/lib/models/use-model-label';

// 图像重绘可用模型：通过 tag 从模型注册表解析，避免硬编码模型 ID。
export const REPAINT_MODEL_IDS = getImageModelIdsByTag(IMAGE_MODEL_TAGS.inpaint);

// 遮罩预览着色（半透明紫色，比白色在大多数图片上更醒目）
const OVERLAY_COLOR = 'rgba(139, 92, 246, 0.55)';
const PROMPT_MAX_LEN = 140;

// 撤销基于操作历史回放（只存笔迹坐标，不存像素快照，避免大图下内存暴涨）
type MaskOp =
  | { type: 'stroke'; erase: boolean; width: number; points: { x: number; y: number }[] }
  | { type: 'invert' }
  | { type: 'clear' };

type RepaintDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  imageUrl: string;
  /** 确认重绘：mask 为遮罩 dataURL（白=保留，黑=重绘） */
  onRedraw: (maskDataUrl: string, prompt: string, modelId: string, resolution?: string) => void;
};

export default function RepaintDialog({ open, onOpenChange, imageUrl, onRedraw }: RepaintDialogProps) {
  const { t } = useI18n();
  // 可见覆盖层（仅做半透明预览）
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // 逻辑遮罩（离屏画布，原图像素尺寸；白=重绘笔迹，透明=保留；导出时再转换）
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // 笔刷光标（直接操作 DOM，避免 pointermove 触发 React 重渲染）
  const brushCursorRef = useRef<HTMLDivElement | null>(null);
  const opsRef = useRef<MaskOp[]>([]);
  const isDrawingRef = useRef(false);
  const [opsCount, setOpsCount] = useState(0);
  const [brushSize, setBrushSize] = useState<number>(40);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [prompt, setPrompt] = useState('');
  const [maskDirty, setMaskDirty] = useState<boolean>(false);

  // 模型与分辨率选择（与顶部工具栏弹层一致）
  const [modelId, setModelId] = useState<string | undefined>(REPAINT_MODEL_IDS[0]);
  const resolutions = useMemo<string[]>(() => {
    const model = modelId ? (imageModels as any)[modelId] : undefined;
    return (getEffectiveImageConfig(model, 'i2i').resolutions as string[] | undefined) ?? [];
  }, [modelId]);
  const [resolution, setResolution] = useState<string | undefined>(undefined);
  useEffect(() => {
    setResolution(resolutions[0]);
  }, [resolutions]);
  const getLabelById = useModelLabelById();
  const modelLabel = (mid: string) => getLabelById(mid) ?? mid;

  const redrawOverlayFromMask = useCallback(() => {
    const overlay = canvasRef.current;
    const mask = maskCanvasRef.current;
    if (!overlay || !mask) return;
    const w = overlay.width;
    const h = overlay.height;
    const octx = overlay.getContext('2d');
    if (!octx) return;
    // 清空可见层并以固定透明度着色遮罩（避免笔迹叠加越画越深）
    octx.clearRect(0, 0, w, h);
    octx.drawImage(mask, 0, 0, w, h);
    octx.globalCompositeOperation = 'source-in';
    octx.fillStyle = OVERLAY_COLOR;
    octx.fillRect(0, 0, w, h);
    octx.globalCompositeOperation = 'source-over';
  }, []);

  // 遮罩是否有内容：缩到 48px 小画布上检查 alpha，避免对原图尺寸做整幅像素扫描
  const computeMaskDirty = useCallback((): boolean => {
    const mask = maskCanvasRef.current;
    if (!mask) return false;
    const s = 48;
    const temp = document.createElement('canvas');
    temp.width = s;
    temp.height = s;
    const tctx = temp.getContext('2d', { willReadFrequently: true });
    if (!tctx) return true;
    tctx.drawImage(mask, 0, 0, s, s);
    const data = tctx.getImageData(0, 0, s, s).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  }, []);

  // 在遮罩上执行单个操作（绘制与回放共用，保证撤销后结果一致）
  const applyOp = useCallback((mctx: CanvasRenderingContext2D, op: MaskOp, maskW: number, maskH: number) => {
    if (op.type === 'clear') {
      mctx.clearRect(0, 0, maskW, maskH);
      return;
    }
    if (op.type === 'invert') {
      mctx.globalCompositeOperation = 'source-out';
      mctx.fillStyle = '#ffffff';
      mctx.fillRect(0, 0, maskW, maskH);
      mctx.globalCompositeOperation = 'source-over';
      return;
    }
    if (op.points.length === 0) return;
    mctx.beginPath();
    mctx.lineWidth = op.width;
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';
    mctx.globalCompositeOperation = op.erase ? 'destination-out' : 'source-over';
    mctx.strokeStyle = '#ffffff';
    const [first, ...rest] = op.points;
    mctx.moveTo(first.x, first.y);
    if (rest.length === 0) {
      mctx.lineTo(first.x + 0.01, first.y + 0.01);
    } else {
      for (const p of rest) mctx.lineTo(p.x, p.y);
    }
    mctx.stroke();
    mctx.globalCompositeOperation = 'source-over';
  }, []);

  const replayOps = useCallback(() => {
    const mask = maskCanvasRef.current;
    const mctx = mask?.getContext('2d');
    if (!mask || !mctx) return;
    mctx.clearRect(0, 0, mask.width, mask.height);
    for (const op of opsRef.current) applyOp(mctx, op, mask.width, mask.height);
    redrawOverlayFromMask();
  }, [applyOp, redrawOverlayFromMask]);

  const pushOp = (op: MaskOp) => {
    opsRef.current.push(op);
    setOpsCount(opsRef.current.length);
  };

  const undo = useCallback(() => {
    if (opsRef.current.length === 0) return;
    opsRef.current.pop();
    setOpsCount(opsRef.current.length);
    replayOps();
    setMaskDirty(computeMaskDirty());
  }, [replayOps, computeMaskDirty]);

  // 加载图片并初始化画布
  useEffect(() => {
    if (!open) return;
    setPrompt('');
    setMaskDirty(false);
    setTool('brush');
    opsRef.current = [];
    setOpsCount(0);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxW = 620;
      const viewportH = typeof window !== 'undefined' ? window.innerHeight : 1000;
      const maxH = Math.max(320, Math.floor(viewportH * 0.62));
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.max(1, Math.floor(img.width * ratio));
      const h = Math.max(1, Math.floor(img.height * ratio));
      setCanvasSize({ w, h });
      const overlay = canvasRef.current;
      if (overlay) {
        overlay.width = w;
        overlay.height = h;
        overlay.getContext('2d')?.clearRect(0, 0, w, h);
      }
      // 离屏遮罩使用原图像素尺寸，保证导出遮罩与原图一致
      const mask = document.createElement('canvas');
      mask.width = img.width;
      mask.height = img.height;
      maskCanvasRef.current = mask;
      mask.getContext('2d')?.clearRect(0, 0, mask.width, mask.height);
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  // Cmd/Ctrl + Z 撤销
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')) return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, undo]);

  const updateBrushCursor = (visible: boolean, cssX = 0, cssY = 0) => {
    const el = brushCursorRef.current;
    if (!el) return;
    if (!visible) {
      el.style.display = 'none';
      return;
    }
    el.style.display = 'block';
    el.style.width = `${brushSize}px`;
    el.style.height = `${brushSize}px`;
    el.style.transform = `translate(${cssX - brushSize / 2}px, ${cssY - brushSize / 2}px)`;
  };

  const handlePointer = (type: 'down' | 'move' | 'up' | 'leave', e: React.PointerEvent<HTMLCanvasElement>) => {
    const overlay = canvasRef.current;
    const mask = maskCanvasRef.current;
    if (!overlay || !mask) {
      if (type === 'leave') updateBrushCursor(false);
      return;
    }
    const rect = overlay.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    // 指针位置从 CSS 像素映射到原图尺寸的遮罩画布
    const scaleX = mask.width / rect.width || 1;
    const scaleY = mask.height / rect.height || 1;
    const avgScale = (scaleX + scaleY) / 2;
    const x = Math.max(0, Math.min(cssX * scaleX, mask.width));
    const y = Math.max(0, Math.min(cssY * scaleY, mask.height));
    updateBrushCursor(type !== 'leave', cssX, cssY);
    const mctx = mask.getContext('2d');
    if (!mctx) return;
    const isErasing = tool === 'eraser';
    // 笔刷大小按屏幕像素理解，落到遮罩画布时按缩放放大，保证大图小图手感一致
    const maskLineWidth = Math.max(1, brushSize * avgScale);
    if (type === 'down') {
      // 捕获指针：拖出画布再松开也能正确结束本次笔迹
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
      isDrawingRef.current = true;
      pushOp({ type: 'stroke', erase: isErasing, width: maskLineWidth, points: [{ x, y }] });
      mctx.beginPath();
      mctx.lineWidth = maskLineWidth;
      mctx.lineCap = 'round';
      mctx.lineJoin = 'round';
      mctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
      mctx.strokeStyle = '#ffffff';
      mctx.moveTo(x, y);
      mctx.lineTo(x + 0.01, y + 0.01);
      mctx.stroke();
      mctx.globalCompositeOperation = 'source-over';
      redrawOverlayFromMask();
      if (!isErasing) setMaskDirty(true);
    } else if (type === 'move') {
      if (!isDrawingRef.current) return;
      const currentOp = opsRef.current[opsRef.current.length - 1];
      if (currentOp?.type === 'stroke') currentOp.points.push({ x, y });
      mctx.lineWidth = maskLineWidth;
      mctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
      mctx.strokeStyle = '#ffffff';
      mctx.lineTo(x, y);
      mctx.stroke();
      mctx.globalCompositeOperation = 'source-over';
      redrawOverlayFromMask();
    } else if (type === 'up') {
      if (isDrawingRef.current && isErasing) setMaskDirty(computeMaskDirty());
      isDrawingRef.current = false;
      mctx.closePath();
    } else {
      // leave：指针已捕获时 move/up 仍会派发，这里只隐藏光标
      updateBrushCursor(false);
    }
  };

  const clearMask = () => {
    if (!maskDirty) return;
    pushOp({ type: 'clear' });
    replayOps();
    setMaskDirty(false);
  };

  const invertMask = () => {
    pushOp({ type: 'invert' });
    replayOps();
    setMaskDirty(computeMaskDirty());
  };

  // 导出遮罩：白底（保留）+ 黑色笔迹（重绘）
  const getComputedMaskUrl = (maskCanvas: HTMLCanvasElement) => {
    const w = maskCanvas.width;
    const h = maskCanvas.height;
    const temp = document.createElement('canvas');
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext('2d');
    if (!tctx) return maskCanvas.toDataURL('image/png');
    // 1. 全白（保留区）
    tctx.fillStyle = '#ffffff';
    tctx.fillRect(0, 0, w, h);
    // 2. 用笔迹擦掉白底
    tctx.globalCompositeOperation = 'destination-out';
    tctx.drawImage(maskCanvas, 0, 0);
    // 3. 透明处（即笔迹）填黑（重绘区）
    tctx.globalCompositeOperation = 'destination-over';
    tctx.fillStyle = '#000000';
    tctx.fillRect(0, 0, w, h);
    return temp.toDataURL('image/png');
  };

  const canConfirm = maskDirty && prompt.trim().length > 0 && Boolean(modelId);

  const handleConfirm = () => {
    const mask = maskCanvasRef.current;
    if (!mask || !canConfirm || !modelId) return;
    onRedraw(getComputedMaskUrl(mask), prompt.trim(), modelId, resolution);
    onOpenChange(false);
  };

  const sectionLabelCls = 'text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50';
  const toolBtnCls = (active: boolean) =>
    cn(
      'flex-1 h-8 gap-1.5 text-xs font-medium rounded-lg border transition-all',
      active ? 'bg-primary/10 border-primary/20 text-primary shadow-sm' : 'bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent'
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl rounded-2xl bg-sidebar/95 backdrop-blur-2xl border-sidebar-border/30 shadow-2xl p-0 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* 左侧：画布区 */}
          <div className="flex-1 min-w-0 flex items-center justify-center bg-black/20 p-5">
            <div className="relative" style={canvasSize.w > 0 ? { width: canvasSize.w, height: canvasSize.h } : undefined}>
              {canvasSize.w > 0 ? (
                <img
                  src={imageUrl}
                  alt={t('Preview image')}
                  width={canvasSize.w}
                  height={canvasSize.h}
                  className="block rounded-lg select-none"
                  draggable={false}
                />
              ) : null}
              <canvas
                ref={canvasRef}
                width={canvasSize.w}
                height={canvasSize.h}
                className="absolute inset-0 rounded-lg cursor-none"
                style={{ width: '100%', height: '100%', touchAction: 'none' }}
                onPointerDown={(e) => handlePointer('down', e)}
                onPointerMove={(e) => handlePointer('move', e)}
                onPointerUp={(e) => handlePointer('up', e)}
                onPointerLeave={(e) => handlePointer('leave', e)}
              />
              {/* 笔刷光标（通过 ref 直接更新位置，不触发重渲染） */}
              <div
                ref={brushCursorRef}
                className="pointer-events-none absolute left-0 top-0 rounded-full border-2 border-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                style={{
                  display: 'none',
                  backgroundColor: tool === 'eraser' ? 'rgba(255,255,255,0.15)' : OVERLAY_COLOR,
                }}
              />
              {/* 未涂抹时的引导提示 */}
              {!maskDirty && canvasSize.w > 0 ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                  <div className="rounded-full bg-black/55 backdrop-blur px-3 py-1.5 text-[11px] font-medium text-white/90">
                    {t('Paint area hint')}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* 右侧：操作面板 */}
          <div className="w-full md:w-[280px] shrink-0 border-t md:border-t-0 md:border-l border-sidebar-border/30 p-4 flex flex-col gap-3">
            <div className="space-y-1">
              <div className="text-base font-bold tracking-tight text-foreground/90">{t('Image repaint')}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{t('Inpainting hint')}</div>
            </div>

            {/* 工具 */}
            <div className={sectionLabelCls}>{t('Tools')}</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className={toolBtnCls(tool === 'brush')} onClick={() => setTool('brush')}>
                <Brush size={13} />
                {t('Brush')}
              </Button>
              <Button size="sm" variant="ghost" className={toolBtnCls(tool === 'eraser')} onClick={() => setTool('eraser')}>
                <Eraser size={13} />
                {t('Erase')}
              </Button>
            </div>
            <div className="flex items-center gap-2.5">
              <Slider
                value={[brushSize]}
                onValueChange={(v) => setBrushSize(v[0] ?? 30)}
                min={5}
                max={120}
                step={1}
                className="flex-1"
              />
              <span className="w-10 text-right text-[11px] font-semibold text-muted-foreground tabular-nums">{brushSize}px</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 h-8 gap-1.5 text-xs font-medium rounded-lg bg-sidebar-accent/30 hover:bg-sidebar-accent"
                disabled={opsCount === 0}
                onClick={undo}
              >
                <Undo2 size={13} />
                {t('Undo')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 h-8 gap-1.5 text-xs font-medium rounded-lg bg-sidebar-accent/30 hover:bg-sidebar-accent"
                onClick={invertMask}
              >
                <FlipHorizontal2 size={13} />
                {t('Invert')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 h-8 gap-1.5 text-xs font-medium rounded-lg bg-sidebar-accent/30 hover:bg-sidebar-accent"
                disabled={!maskDirty}
                onClick={clearMask}
              >
                <Trash2 size={13} />
                {t('Clear')}
              </Button>
            </div>

            {/* 重绘提示词 */}
            <div className="flex items-center justify-between">
              <div className={sectionLabelCls}>{t('Redraw prompt')}</div>
              <div className="text-[10px] text-muted-foreground/60 tabular-nums">{prompt.length}/{PROMPT_MAX_LEN}</div>
            </div>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt((e.target.value || '').slice(0, PROMPT_MAX_LEN))}
              placeholder={t('Enter prompt for redraw')}
              rows={3}
              maxLength={PROMPT_MAX_LEN}
              className="resize-none text-sm"
            />

            {/* 模型（来自 tag: image-inpaint，仅多于一个时显示） */}
            {REPAINT_MODEL_IDS.length > 1 && (
              <>
                <div className={sectionLabelCls}>{t('Choose model')}</div>
                <div className="flex flex-col gap-1">
                  {REPAINT_MODEL_IDS.map((mid) => {
                    const isSel = modelId === mid;
                    return (
                      <Button
                        key={mid}
                        size="sm"
                        variant="ghost"
                        className={cn(
                          'justify-start h-9 px-2.5 rounded-lg border transition-all text-xs font-medium',
                          isSel ? 'bg-primary/10 border-primary/20 text-primary shadow-sm' : 'bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent'
                        )}
                        onClick={() => setModelId(mid)}
                      >
                        <div className={cn('size-1.5 rounded-full mr-2', isSel ? 'bg-primary animate-pulse' : 'bg-muted-foreground/20')} />
                        {modelLabel(mid)}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}

            {/* 分辨率 */}
            {resolutions.length > 0 && (
              <>
                <div className={sectionLabelCls}>{t('Resolution')}</div>
                <div className="flex items-center gap-1">
                  {resolutions.map((res) => (
                    <Button
                      key={res}
                      size="sm"
                      variant="ghost"
                      className={cn(
                        'flex-1 h-8 text-xs font-medium rounded-lg border transition-all',
                        resolution === res ? 'bg-primary/10 border-primary/20 text-primary shadow-sm' : 'bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent'
                      )}
                      onClick={() => setResolution(res)}
                    >
                      {res}
                    </Button>
                  ))}
                </div>
              </>
            )}

            <div className="mt-auto pt-2 flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => onOpenChange(false)}>
                {t('Cancel')}
              </Button>
              <Button
                size="sm"
                className="rounded-lg px-4 h-8 text-xs font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-95"
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                {t('Confirm')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
